import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import {
  SurfaceClassName,
  SurfaceClassNamePhotobomb,
  SurfaceStyle,
  SurfaceStylePhotobomb,
} from "./buttonSurface";

export type ButtonVariant = "orange" | "heroSky" | "white" | "darkGlass";

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  children?: ReactNode;
  variant?: ButtonVariant;
  /** When `"custom"`, only `landing-sans-copy` is applied; add spacing in `className`. */
  pad?: "default" | "custom";
  href?: string;
  rel?: string;
  target?: string;
  download?: string | boolean;
};

export {
  SurfaceClassName,
  SurfaceClassNamePhotobomb,
  SurfaceStyle,
  SurfaceStylePhotobomb,
} from "./buttonSurface";

/** Clawpify / landing orange bubble tokens */
export const landingOrangeBubbleClassName = SurfaceClassName;
export const landingOrangeBubbleStyle = SurfaceStyle;

/** Photobomb marketing: same chrome, violet ramp + glow */
export const photobombBubbleClassName = SurfaceClassNamePhotobomb;
export const photobombBubbleStyle = SurfaceStylePhotobomb;

export const landingWhiteBubbleClassName = [
  "relative isolate overflow-hidden rounded-full border border-zinc-200/90",
  "bg-gradient-to-b from-white to-zinc-100/95 font-medium text-zinc-800 antialiased",
  "shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_2px_8px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.06)]",
  "transition-[filter,opacity,background-color]",
  "before:pointer-events-none before:absolute before:inset-x-[12%] before:top-px before:z-[1] before:h-[42%] before:rounded-[999px]",
  "before:bg-gradient-to-b before:from-white/55 before:via-white/12 before:to-transparent",
  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:top-[55%] after:rounded-b-full",
  "after:bg-gradient-to-t after:from-black/[0.06] after:to-transparent",
  "hover:border-zinc-300/95 hover:from-zinc-50 hover:to-zinc-100",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2",
  "active:brightness-[0.97]",
  "disabled:cursor-not-allowed disabled:opacity-45",
].join(" ");

const heroSkyStyle = {
  background:
    "linear-gradient(180deg, #38bdf8 0%, #0ea5e9 35%, #0284c7 70%, #0369a1 100%)",
  borderColor: "rgba(14, 116, 185, 0.95)",
  boxShadow: [
    "inset 0 1px 0 0 rgba(255, 255, 255, 0.55)",
    "inset 0 -1px 0 0 rgba(0, 0, 0, 0.12)",
    "0 1px 0 rgba(255, 255, 255, 0.35)",
    "0 8px 22px rgba(3, 105, 161, 0.45)",
    "0 2px 6px rgba(0, 0, 0, 0.12)",
  ].join(", "),
} as const;

const darkGlassClassName = [
  "relative isolate inline-flex shrink-0 items-center justify-center gap-2 rounded-full",
  "border border-white/15 bg-black/40 px-4 py-2.5 text-sm font-semibold text-white antialiased",
  "backdrop-blur-sm transition-[border-color,background-color]",
  "hover:border-white/25 hover:bg-black/55",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#151519]",
  "active:brightness-[0.97]",
].join(" ");

export function Button({
  className = "",
  children = "Download",
  disabled,
  type = "button",
  variant = "orange",
  pad = "default",
  href,
  id,
  rel,
  target,
  "aria-label": ariaLabel,
  download,
  ...rest
}: ButtonProps) {
  const isHero = variant === "heroSky";
  const isWhite = variant === "white";
  const isDarkGlass = variant === "darkGlass";

  if (isDarkGlass) {
    const merged = [
      darkGlassClassName,
      href ? "no-underline" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const inner = (
      <span className="relative z-[2] inline-flex items-center gap-2">
        {children}
      </span>
    );

    if (href) {
      return (
        <a
          id={id}
          href={href}
          className={merged}
          rel={rel}
          target={target}
          aria-label={ariaLabel}
          download={download}
        >
          {inner}
        </a>
      );
    }

    return (
      <button
        id={id}
        type={type}
        disabled={disabled}
        className={merged}
        aria-label={ariaLabel}
        {...rest}
      >
        {inner}
      </button>
    );
  }

  const leadingSurface = isHero
    ? ""
    : isWhite
      ? landingWhiteBubbleClassName
      : SurfaceClassNamePhotobomb;

  const landingCopyPadding =
    pad === "custom"
      ? "landing-sans-copy inline-flex shrink-0 items-center justify-center"
      : "landing-sans-copy inline-flex shrink-0 items-center justify-center min-w-[10rem] px-10 py-3";

  const heroSkyBlock = isHero
    ? [
        "relative isolate overflow-hidden rounded-full font-medium antialiased text-white",
        "border shadow-md transition-[filter,opacity]",
        "before:pointer-events-none before:absolute before:inset-x-[12%] before:top-px before:z-[1] before:h-[42%] before:rounded-[999px]",
        "before:bg-gradient-to-b before:from-white/50 before:via-white/15 before:to-transparent",
        "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:top-[55%] after:rounded-b-full",
        "after:bg-gradient-to-t after:from-black/10 after:to-transparent",
        "disabled:cursor-not-allowed disabled:opacity-45",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#151519]",
        "active:brightness-[0.97]",
      ].join(" ")
    : "";

  const mergedClass = [
    leadingSurface,
    landingCopyPadding,
    heroSkyBlock,
    href ? "no-underline" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const style: CSSProperties | undefined = isHero
    ? heroSkyStyle
    : isWhite
      ? undefined
      : SurfaceStylePhotobomb;

  const inner = (
    <span className="relative z-[2] inline-flex items-center justify-center gap-2">
      {children}
    </span>
  );

  if (href) {
    return (
      <a
        id={id}
        href={href}
        className={mergedClass}
        style={style}
        rel={rel}
        target={target}
        aria-label={ariaLabel}
        download={download}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      id={id}
      type={type}
      disabled={disabled}
      className={mergedClass}
      style={style}
      aria-label={ariaLabel}
      {...rest}
    >
      {inner}
    </button>
  );
}
